import { IPersonaStyles } from '@fluentui/react';
import React, { Fragment, FunctionComponent } from 'react';

import AppContext from 'App/AppContext';
import { IPIconButton, IPScrollablePane, IPSpinner, IPText } from 'baseComponents';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { SkypeCircleCheckIcon } from 'components/Checklist/CheckListStyles';
import SingleTask from 'components/Tasks/SingleTask';
import DateDividerListItem, {
  DataGroup,
  DateComparer,
  DateDivider,
  insertDividersAndGroupsInList,
} from 'components/Utils/ListItemDateDividers';
import Task from 'models/tasks/task';
import { useTranslation } from 'react-i18next';
import { toLocaleDateTimeMedium } from 'utils/datetime';
import { sortOnDate } from 'utils/sorting';
import { toNameInitial } from 'utils/string';
import { IWidgetRendererProps } from '../WidgetRenderer';
import { IPList } from "baseComponents/List/IPList";

interface IWidgetMyOverdueTasksProps extends IWidgetRendererProps {}

const WidgetMyOverdueTasks: FunctionComponent<IWidgetMyOverdueTasksProps> = (props: IWidgetMyOverdueTasksProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = React.useContext(AppContext);
  const [tasks, setTasks] = React.useState<Task[]>([]);
  const [tasksDisplay, setTasksDisplay] = React.useState<(Task | DateDivider | DataGroup<Task>)[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [selectedTask, setSelectedTask] = React.useState<Task | undefined>(undefined);

  React.useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget, props.sharedData]);

  React.useEffect(() => {
    const getCompareDate: DateComparer<Task> = (a) => {
      return a.getDeadline(appContext.globalDataCache);
    };

    if (tasks && tasks?.length > 0) {
      const newData = insertDividersAndGroupsInList(tasks, getCompareDate, undefined, undefined);
      setTasksDisplay(newData);
    }
  }, [appContext.globalDataCache, tasks]);

  const loadData = async (refresh?: boolean) => {
    try {
      if (props.onRefreshSharedData && props.widget.dashboard && refresh) {
        await props.onRefreshSharedData();

        return;
      }

      if (isLoading) return;
      setIsLoading(true);

      const taskCol = props.sharedData?.myTasks ?? [];

      const filteredTasks = taskCol
        .filter(
          (t) => t.completed === undefined && t.getMinutesOverDeadline(new Date(), appContext.globalDataCache) > 0,
        )
        .sort((a, b) =>
          sortOnDate(a.getDeadline(appContext.globalDataCache), b.getDeadline(appContext.globalDataCache)),
        );

      setTasks(filteredTasks);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const personaStyle: Partial<IPersonaStyles> = {
    root: {
      marginBottom: 5,
      selectors: {
        '&:hover': {
          cursor: 'pointer',
        },
      },
    },
  };

  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  //dismissing the modal is close's job: SingleTask also saves WITHOUT closing (a task type pane that
  //needs the stored task first), and clearing the selection here unmounts it in the middle of that
  const saveTask = async (task: Task, isNew: boolean, isCancelled: boolean) => {
    if (!isCancelled) {
      await loadData(true);
    }
  };

  const removeTask = async (task: Task) => {
    setSelectedTask(undefined);
    await loadData(true);
  };

  const updateTask = async (task: Task) => {};

  return (
    <IPStack verticalFill styles={{ root: { paddingTop: 5 } }}>
      <IPStack.Item grow styles={{ root: { position: 'relative' } }}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <IPList
            items={tasksDisplay}
            onRenderCell={(task) => {
              if (!task) return null;

              if (task instanceof DateDivider) {
                return <DateDividerListItem {...task} />;
              }

              if (task instanceof DataGroup) {
                return null;
              }

              return (
                <IPPersona
                  size={IPPersonaSize.size40}
                  text={task.name}
                  imageInitials={toNameInitial(appContext.globalDataCache.users.get(task.userId).name)}
                  secondaryText={toLocaleDateTimeMedium(task.getDeadline(appContext.globalDataCache))}
                  onClick={() => {
                    if (!task) return;

                    setSelectedTask(task);
                  }}
                  styles={personaStyle}
                />
              );
            }}
          />
        </IPScrollablePane>
      </IPStack.Item>
      {tasks?.length === 0 && (
        <Fragment>
          <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
            <IPStack.Item>
              <IPIconButton iconProps={SkypeCircleCheckIcon} />
            </IPStack.Item>
          </IPStack>
          <IPStack.Item>
            <IPText>{t('widgets:MyOverdueTasks.NoItems')}</IPText>
          </IPStack.Item>
        </Fragment>
      )}
      {selectedTask && (
        <SingleTask
          task={selectedTask}
          isOpen={true}
          close={() => {
            setSelectedTask(undefined);
          }}
          onSave={saveTask}
          onRemove={removeTask}
          onUpdate={updateTask}
        />
      )}
    </IPStack>
  );
};

export default WidgetMyOverdueTasks;
